import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/auth/AuthContext";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import LanguagePicker from "@/components/LanguagePicker";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import DobWheelPicker, { ageFromIso } from "@/components/DobWheelPicker";
const GENDER_KEYS = [{
  id: "male",
  labelKey: "auth.gender_male"
}, {
  id: "female",
  labelKey: "auth.gender_female"
}, {
  id: "non_binary",
  labelKey: "auth.gender_non_binary"
}, {
  id: "prefer_not",
  labelKey: "auth.gender_prefer_not"
}];
function passwordStrength(pw, t) {
  if (!pw) return {
    score: 0,
    label: ""
  };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const labels = [t("auth.strength_too_weak"), t("auth.strength_weak"), t("auth.strength_fair"), t("auth.strength_good"), t("auth.strength_strong"), t("auth.strength_excellent")];
  return {
    score: s,
    label: labels[s] || ""
  };
}
export default function Register() {
  const nav = useNavigate();
  const {
    t,
    i18n
  } = useTranslation();
  const {
    register
  } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [gender, setGender] = useState("prefer_not");
  // Default DOB ≈ 30 years ago, Jan 1 (locale-agnostic).
  const defaultDob = (() => {
    const y = new Date().getFullYear() - 30;
    return `${y}-01-01`;
  })();
  const [dob, setDob] = useState(defaultDob);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const strength = passwordStrength(password, t);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const matches = confirm === password && confirm.length > 0;
  const dobAge = ageFromIso(dob);
  const dobOk = dobAge != null && dobAge >= 13 && dobAge <= 100;
  const canSubmit = firstName.trim() && lastName.trim() && emailOk && strength.score >= 2 && matches && accepted && dobOk;
  const submit = async e => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      await register(email.trim(), password, fullName, gender, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        accepted_terms: true,
        dob
      });
      toast.success(t("auth.welcome_to_wing"));
      nav("/onboarding/health-sources");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("auth.register_failed"));
    } finally {
      setBusy(false);
    }
  };
  return <div className="app-shell" data-testid="register-page">
            <div className="relative w-full aspect-[3/2]">
                <div className="absolute inset-0 overflow-hidden">
                    <img src="/wing-hero.png" alt={t("register.wyng")} className="w-full h-full object-cover" style={{
          objectPosition: "center 40%"
        }} data-testid="register-hero-image" />
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-background" />
                </div>
                <div className="absolute left-4 top-12">
                    <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full bg-background/40 backdrop-blur border border-border flex items-center justify-center" data-testid="register-back">
                        <ArrowLeft size={18} />
                    </button>
                </div>
                <div className="absolute right-4 top-12 z-10">
                    <LanguagePicker />
                </div>
            </div>

            <div className="px-6 -mt-6 pb-10 relative">
                <h1 className="font-display text-4xl font-semibold tracking-tighter">{t("auth.register_title")}</h1>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{t("auth.register_subtitle")}</p>

                <div className="mt-6">
                    <SocialAuthButtons mode="register" />
                </div>

                <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="h-px flex-1 bg-border" />
                    <span>{t("common.or_email")}</span>
                    <div className="h-px flex-1 bg-border" />
                </div>

                <form onSubmit={submit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <Input label={t("auth.first_name")} value={firstName} onChange={setFirstName} testId="register-first" required />
                        <Input label={t("auth.last_name")} value={lastName} onChange={setLastName} testId="register-last" required />
                    </div>
                    <Input label={t("auth.email")} type="email" value={email} onChange={setEmail} testId="register-email" required />
                    {email && !emailOk && <p className="text-[11px] text-red-500">{t("auth.invalid_email")}</p>}

                    <Input label={t("auth.password_hint")} type="password" value={password} onChange={setPassword} testId="register-password" required minLength={8} />
                    {password && <div className="flex items-center gap-2" data-testid="register-strength">
                            <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                                <div className={`h-full transition-all ${strength.score < 2 ? "bg-red-500" : strength.score < 4 ? "bg-[#FF9F0A]" : "bg-[#32D74B]"}`} style={{
              width: `${strength.score / 5 * 100}%`
            }} />
                            </div>
                            <span className="text-[11px] text-muted-foreground w-20 text-right">{strength.label}</span>
                        </div>}

                    <Input label={t("auth.confirm_password")} type="password" value={confirm} onChange={setConfirm} testId="register-confirm" required />
                    {confirm && !matches && <p className="text-[11px] text-red-500" data-testid="register-mismatch">{t("auth.passwords_dont_match")}</p>}

                    <div>
                        <span className="metric-label">{t("auth.gender_label")}</span>
                        <div className="grid grid-cols-2 gap-2 mt-1.5">
                            {GENDER_KEYS.map(g => {
              return <button key={g.id} type="button" onClick={() => setGender(g.id)} data-testid={`gender-${g.id}`} className={`rounded-2xl border px-3 py-3 text-sm font-semibold transition ${gender === g.id ? "bg-foreground text-background border-foreground" : "border-border bg-card text-foreground/80"}`}>
                                    {t(g.labelKey)}
                                </button>;
            })}
                        </div>
                    </div>

                    <div data-testid="register-dob-section">
                        <div className="flex items-baseline justify-between">
                            <span className="metric-label">{t("auth.dob_label")}</span>
                            {dobAge != null && <span className="text-[11px] text-muted-foreground tabular-nums" data-testid="register-dob-age">
                                    {t("auth.dob_age", {
                age: dobAge
              })}
                                </span>}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
                            {t("auth.dob_hint")}
                        </p>
                        <div className="rounded-2xl border border-border bg-card p-2">
                            <DobWheelPicker value={dob} onChange={setDob} locale={i18n.language} />
                        </div>
                        {!dobOk && <p className="text-[11px] text-red-500 mt-1" data-testid="register-dob-error">
                                {t("auth.dob_invalid")}
                            </p>}
                    </div>

                    <label className="flex items-start gap-3 pt-2 cursor-pointer" data-testid="register-terms-label">
                        <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} data-testid="register-terms" className="mt-0.5 h-5 w-5 rounded-md accent-foreground" />
                        <span className="text-[12px] text-muted-foreground leading-snug">
                            {t("auth.terms_prefix")}{" "}
                            <Link to="/terms" className="text-foreground font-semibold underline underline-offset-2">{t("auth.terms_link")}</Link>
                            {" "}{t("auth.and")}{" "}
                            <Link to="/privacy" className="text-foreground font-semibold underline underline-offset-2">{t("auth.privacy_link")}</Link>.
                        </span>
                    </label>

                    <button type="submit" disabled={busy || !canSubmit} data-testid="register-submit" className="w-full rounded-full bg-foreground text-background py-4 font-semibold active:scale-[0.98] transition disabled:opacity-50">
                        {busy ? t("auth.creating") : t("auth.submit_register")}
                    </button>
                </form>
                <div className="mt-6 text-sm text-muted-foreground text-center">
                    {t("auth.have_account")}{" "}
                    <Link to="/login" className="text-foreground font-semibold" data-testid="register-to-login">
                        {t("auth.sign_in")}
                    </Link>
                </div>
            </div>
        </div>;
}
function Input({
  label,
  value,
  onChange,
  type = "text",
  testId,
  ...rest
}) {
  return <label className="block">
            <span className="metric-label">{label}</span>
            <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} className="mt-1.5 w-full bg-card border border-border rounded-2xl px-4 py-3.5 text-base outline-none focus:border-foreground/40 transition" {...rest} />
        </label>;
}